import Graph from './graph';
import Boxes from './boxes';
import ActivityChart from './activity-chart';
import AltActivityChart from './alt-activity-chart';
import { fetchApiUrl } from '../utilities';
const d3 = require('d3');
const baseUrl = '/api/v1/';

export default function (chartEl) {
  let prices, graphObj, boxesObj, manuallySelectedTimePeriod, activeTimePeriod;
  let possibleTimePeriods = [1, 3, 5, 15]; // years

  const stockId = JSON.parse(chartEl.getAttribute('data-stock-id'));
  const guruId = chartEl.getAttribute('data-guru-id');
  const politicianId = chartEl.getAttribute('data-politician-id');
  const transactionType = chartEl.getAttribute('data-transaction-type');
  const showActivityChart = chartEl.getAttribute('data-show-activity-chart');
  const showAltActivityChart = chartEl.getAttribute('data-show-alt-activity-chart');

  let transactionsUrl =
    baseUrl +
    'transactions?id=' +
    stockId +
    '&guru_id=' +
    (guruId && JSON.parse(guruId)) +
    '&transaction_type=' +
    transactionType;

  if (politicianId) {
    transactionsUrl = '/congress-stock-trades/transactions?politician_id=' + politicianId + '&id=' + stockId;
  }

  let pricesUrl = baseUrl + 'prices?id=' + stockId;

  if (showAltActivityChart) {
    const urlParams = new URLSearchParams(window.location.search);
    let stockSymbol = urlParams.get('stock');
    if (!stockSymbol) {
      stockSymbol = 'AAPL';
    }
    pricesUrl = baseUrl + 'prices?stock=' + stockSymbol;
  }

  const fetches = [];
  if (guruId || politicianId) {
    fetches.push(fetchApiUrl(transactionsUrl));
  } else {
    fetches.push(Promise.resolve({ json: () => [] }));
  }
  fetches.push(fetchApiUrl(pricesUrl));

  Promise.all(fetches).then(
    function (data) {
      Promise.all([data[0].json(), data[1].json()]).then(function (data) {
        // check if chartEl is still in the DOM, fix bug on certrain stock pages
        if (!document.contains(chartEl) && document.querySelectorAll('.line-chart').length === 1) {
          chartEl = document.querySelector('.line-chart');
        }

        let boxes = data[0];
        boxes = formatDates(boxes, ['start_date', 'end_date']);
        prices = data[1];
        prices = formatDates(prices, ['date']);

        const filteredPrices = filterByDates(prices, 'date');
        const yAxisLimits = getYAxisLimits(filteredPrices);

        if (showAltActivityChart) {
          possibleTimePeriods = [1];
        } else {
          addTimePeriodSwitch();
        }

        graphObj = new Graph(chartEl, filteredPrices, boxes, yAxisLimits);
        if (guruId || politicianId) {
          boxesObj = new Boxes(
            chartEl,
            boxes,
            filteredPrices,
            yAxisLimits
          );
        }

        if (showActivityChart) {
          // first load prices and now boxes on stock pages
          fetchApiUrl(transactionsUrl).then(function (data) {
            data.json().then(function (boxes) {
              boxes = formatDates(boxes, ['start_date', 'end_date']);
              boxesObj = new ActivityChart(
                chartEl,
                boxes,
                filteredPrices,
                activeTimePeriod
              );
            });
          });
        } else if (showAltActivityChart) {
          const urlParams = new URLSearchParams(window.location.search);
          const politician = urlParams.get('politician');
          const stock = urlParams.get('stock');
          const party = urlParams.get('party');
          const activityUrl = baseUrl + 'politicians/activity?politician=' + politician + '&stock=' + stock + '&party=' + party + '&politician_id=' + politicianId;

          fetchApiUrl(activityUrl).then(function (data) {
            data.json().then(function (boxes) {
              boxesObj = new AltActivityChart(
                chartEl,
                boxes,
                filteredPrices,
                activeTimePeriod
              );
            });
          });
        }

        const lastBox = boxes[boxes.length - 1];
        const activeBoxes = boxes.filter(
          (box) => box.date_formatted === lastBox.date_formatted
        );
        graphObj.showTransactionDetailsOf(activeBoxes);

        window.addEventListener('resize', resize);
      });
    }
  );

  let previousUrl = window.location.href;
  if (showAltActivityChart) {
    // for congress trades update chart when filters change
    const observer = new MutationObserver(function () {
      if (window.location.href !== previousUrl) {
        previousUrl = window.location.href;
        const urlParams = new URLSearchParams(window.location.search);
        const politician = urlParams.get('politician');
        let stock = urlParams.get('stock');
        const party = urlParams.get('party');
        const activityUrl = baseUrl + 'politicians/activity?politician=' + politician + '&stock=' + stock + '&party=' + party;

        if (!stock) {
          stock = 'AAPL';
        }
        const pricesUrl = baseUrl + 'prices?stock=' + stock;

        Promise.all([fetchApiUrl(pricesUrl), fetchApiUrl(activityUrl)]).then(function (data) {
          Promise.all([data[0].json(), data[1].json()]).then(function (data) {
            prices = data[0];
            prices = formatDates(prices, ['date']);
            let boxes = data[1];
            const filteredPrices = filterByDates(prices, 'date');
            boxesObj.reset(filteredPrices, boxes);

            const yAxisLimits = getYAxisLimits(filteredPrices);
            graphObj.resize(filteredPrices, yAxisLimits);

            if (urlParams.get('stock') && prices?.length > 0) {
              chartEl.classList.remove('line-chart--hide-chart');
              const badge = document.querySelector('.js-line-chart-badge');
              badge.innerText = stock + ' Price';
              badge.href = '/stocks/' + stock.toLowerCase();
            } else {
              chartEl.classList.add('line-chart--hide-chart');
            }
          });
        });
      }
    });
    const config = { subtree: true, childList: true };
    observer.observe(document, config);
  }

  function resize() {
    const filteredPrices = filterByDates(prices, 'date');
    const yAxisLimits = getYAxisLimits(filteredPrices);
    graphObj.resize(filteredPrices, yAxisLimits);
    if (boxesObj) {
      boxesObj.resize(filteredPrices, yAxisLimits, activeTimePeriod);
    }
    chartEl
      .querySelector('.chart-switch__item--active')
      .classList.remove('chart-switch__item--active');
    chartEl
      .querySelector('.js-line-chart-time-' + activeTimePeriod)
      .classList.add('chart-switch__item--active');
  }

  function addTimePeriodSwitch() {
    const switchContainer = d3
      .select(chartEl)
      .append('div')
      .attr('class', 'chart-switch');

    for (let years of possibleTimePeriods) {
      const classNames = [
        'chart-switch__item',
        'js-line-chart-time-' + years,
      ];

      if (years == activeTimePeriod) {
        classNames.push('chart-switch__item--active');
      }

      const time = switchContainer
        .append('a')
        .attr('class', classNames.join(' '))
        .html(years + 'Y');
      time.node().addEventListener('click', changeTime);
    }
  }

  function filterByDates(data, column) {
    const one_year = 1000 * 60 * 60 * 24 * 365; // ms
    const years = getYears();
    activeTimePeriod = years;

    const years_ago = one_year * years;
    return data.filter(function (d) {
      return new Date() - new Date(d[column]) < years_ago;
    });
  }

  function getYears() {
    if (showAltActivityChart || (politicianId && !manuallySelectedTimePeriod)) {
      // increase once we have more historical data
      return 1;
    }

    const chartWidth = chartEl.clientWidth;
    if (manuallySelectedTimePeriod) {
      return manuallySelectedTimePeriod;
    }

    let years = 5;

    if (chartWidth < 700) {
      years = 1;
    } else if (chartWidth < 1100) {
      years = 3;
    }

    return years;
  }

  function changeTime(event) {
    manuallySelectedTimePeriod = parseInt(event.target.innerText);
    resize();
  }
}

function formatDates(data, keys) {
  return data.map(function (date) {
    for (let key of keys) {
      date[key] = new Date(date[key]);
    }
    return date;
  });
}

function getYAxisLimits(prices) {
  const minYValue = d3.min(prices, function (d) {
    return d.close;
  });
  const maxYValue = d3.max(prices, function (d) {
    return d.close;
  });
  const diff = maxYValue - minYValue;

  return {
    max: maxYValue + diff * 0.25,
    min: minYValue - diff * 0.05,
  };
}
